import { useEffect, useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { FileSpreadsheet, ChevronRight } from "lucide-react";
import { call, date, money } from "@/lib/api";

export function RabCurvePanel({ projectId, onOpenRab }) {
  const [s, setS] = useState(null);
  useEffect(() => { call.get(`/projects/${projectId}/s-curve`).then(setS).catch(() => {}); }, [projectId]);
  if (!s || !s.has_rab) return null;
  const today = new Date().toISOString().slice(0, 10);
  const tone = s.deviation < -5 ? "risk" : s.deviation > 2 ? "ahead" : "ok";
  const label = s.deviation < -5 ? "Terlambat" : s.deviation > 2 ? "Lebih cepat" : "Sesuai rencana";
  return (
    <div className="panel" data-testid="rab-curve-panel">
      <div className="panel-head">
        <div><span className="eyebrow">RAB DASAR PROJECT</span><h3><FileSpreadsheet size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />Kurva S berbobot RAB · {s.rab.code}</h3></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={`sched-badge ${tone}`} data-testid="rab-curve-status">{label} · {s.deviation > 0 ? "+" : ""}{s.deviation}%</span>
          {onOpenRab && <button className="ghost-btn small" data-testid="rab-curve-open-rab" onClick={() => onOpenRab(s.rab.id)}>Lihat RAB <ChevronRight size={13} /></button>}
        </div>
      </div>
      <div className="detail-strip" style={{ marginTop: 14 }}>
        <div><span>NILAI RAB</span><b>{money(s.rab.grand_total)}</b></div>
        <div><span>RENCANA HARI INI</span><b>{s.planned_today}%</b></div>
        <div><span>PROGRESS TERTIMBANG</span><b data-testid="rab-weighted-progress">{s.weighted_progress}%</b></div>
        <div><span>DAILY REPORT</span><b>{s.report_count} laporan</b></div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={s.points}>
          <CartesianGrid vertical={false} stroke="#e6eaf0" />
          <XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString("id-ID", { month: "short", day: "numeric" })} axisLine={false} tickLine={false} tick={{ fill: "#8993a4", fontSize: 10 }} minTickGap={30} />
          <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: "#8993a4", fontSize: 10 }} tickFormatter={v => `${v}%`} />
          <Tooltip formatter={(v, n) => [`${v}%`, n === "planned" ? "Rencana" : "Aktual (bobot RAB)"]} labelFormatter={d => date(d)} contentStyle={{ fontSize: 12, border: "1px solid #e5e9ef" }} />
          <ReferenceLine x={s.points.find(c => c.date >= today)?.date} stroke="#f59e0b" strokeDasharray="4 4" />
          <Line type="monotone" dataKey="planned" stroke="#101827" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="actual" stroke="#2563eb" strokeWidth={3} dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="boq-wrap" style={{ marginTop: 12 }}>
        <table className="boq-table" data-testid="rab-curve-sections">
          <thead><tr><th style={{ width: 40 }}>No</th><th>Jenis Pekerjaan (RAB)</th><th className="num">Nilai</th><th className="num" style={{ width: 80 }}>Bobot</th><th className="num" style={{ width: 90 }}>Progress</th><th className="num" style={{ width: 90 }}>Kontribusi</th><th style={{ width: 110 }}>Laporan terakhir</th></tr></thead>
          <tbody>
            {s.sections.map(x => (
              <tr key={x.no} data-testid={`rab-curve-section-${x.no}`}>
                <td>{x.no}</td><td>{x.name}</td><td className="num">{money(x.subtotal)}</td><td className="num">{x.weight}%</td>
                <td className="num"><span className="progress-inline"><i style={{ width: `${x.progress}%` }} /></span>{x.progress}%</td>
                <td className="num">{x.contribution}%</td><td>{x.last_report ? `${date(x.last_report)} · ${x.reports} lap.` : <span className="muted">belum ada</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="boq-grand"><td colSpan={3}>PROGRESS KESELURUHAN (Σ bobot × progress)</td><td className="num">100%</td><td /><td className="num">{s.weighted_progress}%</td><td /></tr></tfoot>
        </table>
      </div>
      {s.unmatched_work_types.length > 0 && <small className="muted" style={{ display: "block", marginTop: 8 }} data-testid="rab-curve-unmatched">Jenis pekerjaan di daily report yang tidak cocok dengan RAB (tidak dihitung): {s.unmatched_work_types.join(", ")}. Gunakan nama jenis pekerjaan RAB saat mengisi daily report.</small>}
    </div>
  );
}
